/*
 * Copyright (c) 2023. Data Smart Technologies CC
 * https://www.datasmart.co.za
 * View license agreement and terms of use at https://www.datasmart.co.za/legal
 *
 * @author  Juan <juan@datasmart.co.za>
 * @version 1.0
 * @updated 2023-07-11
 */

import React, { useEffect, useState } from "react";
import { SpinnerLoading } from "../utils/SpinnerLoading";
import { ErrorMessage } from "../utils/ErrorMessage";
import { fetchRecords, formatCurrency, htmlCodeAssist } from "../utils/AppUtil";

class Donation {
    date: string;
    donor: string;
    deceased: string;
    isLeaf: boolean;
    currency: string;
    amount: number;
    constructor(date: string, donor: string, deceased: string, isLeaf: boolean, currency: string, amount: number) {
        this.date = date;
        this.donor = donor;
        this.deceased = deceased;
        this.isLeaf = isLeaf;
        this.currency = currency;
        this.amount = amount;
    }
}

type RecentDonorProps = {
    endpoint: string; 
    recordCount: number;
}

export const RecentDonorsMobile: React.FC<RecentDonorProps> = (props): JSX.Element => {

    const [records, setRecords] = useState<Donation[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [httpError, setHttpError] = useState(null);
    const [endpoint, setEndpoint] = useState(`${props.endpoint}?count=${props.recordCount}`);

    // Do not change this or put any additional code in here without checking with
    // the team first.
    useEffect(() => {

        fetchRecords(endpoint, processData)
        .catch((error) => {
            setIsLoading(false);
            setHttpError(error.message);
        });

    }, [endpoint]);

    // You may edit this function to manipulate the way your code is processed.
    const processData = (responseJson: any) => { // Do not edit these parameters, just the content of this function.
        const recordData: Donation[] = [];
    
        for (const key in responseJson) {
            const record: any = responseJson[key];
            const displayDate: string = record["transactionDateDisplay"];
    
            let deceased: string = "";
            let donor: string = "Anonymous";
            let isAnonymous: boolean = record["isAnonymous"]

            const isLeaf: boolean = record["isLeaf"];
            const currency = record["currencyDisplaySymbol"];
            const amount:number = record["amount"];

            if (isLeaf) {

                if (!isAnonymous) {
                    donor = (record["subscriberFirstName"] ? record["subscriberFirstName"] : "") + " " + (record["subscriberLastName"] ? record["subscriberLastName"] : "");
                }
        
                deceased = (record["deceasedFirstName"] ? record["deceasedFirstName"] : " ") + " " + (record["deceasedLastName"] ? record["deceasedLastName"] : "");
                if (!record["deceasedId"]) {
                    deceased = "Not Specified";
                }

            } else {
                donor = (record["donorName"]);

                deceased = (record["donorMessage"]);
                if (!deceased) {
                    deceased = "Not Specified";
                }
            }



            if(amount > 0) {
                recordData.push(new Donation(displayDate, donor, deceased, isLeaf, currency, amount));
            }
        }
        setRecords(recordData);
        setIsLoading(false);
    }

    if(isLoading) {
        return(
            <SpinnerLoading />
        )
    }

    if(httpError) {
        return(
            <ErrorMessage error={httpError} />
        )
    }

    return (
        <div className="card card-recent-funerals jc-border-12">
            <div className="card-header card-header-bg">
                <p className="jc-txt-heading fs-5">Our Most Recent Donors</p>
            </div>

            <div className="card-body p-0">
                <div className="jc-notification-wrapper p-3">
                    <div className="jc-notification d-flex align-items-center gap-2 jc-txt-body fs-6 px-3 py-2">
                        <img src="/images/icon-info.png" />
                        Donations can be made anonymously
                    </div>
                </div>
                <div className="table-donors">
                    <table className="table table-striped mb-0">
                        <tbody>
                            <RecentDonorsRecords records={records} />
                        </tbody>
                    </table>
                </div>
                
                <div className="card-footer d-flex justify-content-end">
                    <a href="/donate" className="btn btn-dark jc-txt-body jc-txt-light">Donate</a>
                </div>

            </div>
        </div>
    );
}

const RecentDonorsRecords: React.FC<{records: Donation[];}>= ({records}): JSX.Element => {

    const result: React.ReactElement[] = [];

    records.forEach((donation, index) => {
        result.push(
            <tr key={index} style={{minHeight: "80px"}}>
                <td className="d-flex">

                    <div className="d-flex flex-column justify-content-between w-50">
                        <p className="jc-txt-body fs-6">{donation.date}</p>
                        <div className="d-flex flex-column">
                            <p className="jc-txt-body" style={{fontSize: "12px", lineHeight: "10px"}}>Donated by:</p>
                            <p className="jc-txt-body fs-6">{donation.donor}</p>
                        </div>
                    </div>

                    <div className="d-flex flex-column justify-content-between w-50">
                        <p className="jc-txt-body fs-5 donation-color mb-2" style={{lineHeight: "24px"}}>
                            {donation.isLeaf && 
                                <svg className="me-1" width="17" height="24" viewBox="0 0 17 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M1.47726 14.8598C3.50052 20.9063 7.65016 23.1812 11.4904 22.1822C15.3258 21.1831 17.408 17.2905 15.3798 11.244C13.3565 5.20192 4.15366 0.292969 4.15366 0.292969C4.15366 0.292969 -0.545997 8.81768 1.47726 14.8598Z" fill="#438519"/>
                                    <path d="M1.47726 14.8598C3.50052 20.9063 7.65016 23.1812 11.4904 22.1822L4.15366 0.292969C4.15366 0.292969 -0.545998 8.81768 1.47726 14.8598Z" fill="#20C997"/>
                                    <path d="M7.93012 5.23047L6.75153 8.04177L7.08055 9.02356L7.93012 5.23047Z" fill="#20C997"/>
                                    <path d="M6.27415 2.90625L5.58173 4.55843L5.77325 5.13366L6.27415 2.90625Z" fill="#20C997"/>
                                    <path d="M10.0203 7.71875L8.14432 12.2082L8.66978 13.7738L10.0203 7.71875Z" fill="#20C997"/>
                                    <path d="M12.4517 11.1367L9.8588 17.3216L10.5856 19.4841L12.4517 11.1367Z" fill="#20C997"/>
                                    <path d="M3.8826 5.25391L6.36747 6.89311L6.66703 7.7884L3.8826 5.25391Z" fill="#438519"/>
                                    <path d="M3.84354 2.75781L5.30205 3.71798L5.47884 4.24564L3.84354 2.75781Z" fill="#438519"/>
                                    <path d="M3.6828 8.07422L7.64092 10.6866L8.11727 12.1138L3.6828 8.07422Z" fill="#438519"/>
                                    <path d="M3.74542 11.7461L9.20134 15.3489L9.85939 17.3168L3.74542 11.7461Z" fill="#438519"/>
                                    <path d="M11.4458 24.0001L12.6539 23.6843L11.5391 21.0547L10.7583 21.258L11.4458 24.0001Z" fill="#20C997"/>
                                    <path d="M11.1471 21.1585L12.0507 23.8444L12.6547 23.6843L11.54 21.0547L11.1471 21.1585Z" fill="#438519"/>
                                </svg>
                            }
                            {htmlCodeAssist(donation.currency)}
                            {formatCurrency(donation.amount)}
                        </p>
                        <div className="d-flex flex-column jc-txt-body">
                            <p style={{fontSize: "12px", lineHeight: "10px"}}>In Memory of:</p>
                            <p className="fs-6">{donation.deceased}</p>
                        </div>
                    </div>

                </td>
            </tr>
        )
    });

    return(
        <>{result}</>
    );
}
